/**
 * PostnlAccounts — Admin paneli: PostNL hesap CRUD UI.
 *
 * Her firma için username/password/label yönetimi. Aktif olanlar
 * Dagplanning & Ritmonitor sayfalarında dropdown/tab olarak görünür.
 */
import { useEffect, useState } from 'react';
import PostnlPageTabs from '../components/PostnlPageTabs';
import { api } from '../lib/auth';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Plus, Loader2, Pencil, Trash2, X, Save, Key, Building2,
  CheckCircle2, XCircle,
} from 'lucide-react';

function emptyForm() {
  return { label: '', username: '', password: '', active: true, notes: '' };
}

export default function PostnlAccounts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | {id...}
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/postnl/credentials');
      setItems(res.data?.data || []);
    } catch {
      toast.error('Liste yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(emptyForm());
    setEditing('new');
  };

  const openEdit = (item) => {
    setForm({
      label: item.label,
      username: item.username,
      password: '', // boş bırakırsa değiştirme
      active: item.active,
      notes: item.notes || '',
    });
    setEditing(item);
  };

  const close = () => {
    setEditing(null);
    setForm(emptyForm());
  };

  const save = async () => {
    if (!form.label.trim() || !form.username.trim()) {
      toast.error('Firma adı ve kullanıcı adı zorunlu');
      return;
    }
    if (editing === 'new' && !form.password.trim()) {
      toast.error('Yeni hesap için şifre zorunlu');
      return;
    }
    setSaving(true);
    try {
      const body = {
        label: form.label.trim(),
        username: form.username.trim(),
        active: form.active,
        notes: form.notes || null,
      };
      if (form.password.trim()) body.password = form.password;
      if (editing === 'new') {
        await api.post('/postnl/credentials', body);
        toast.success('Hesap eklendi');
      } else {
        await api.patch(`/postnl/credentials/${editing.id}`, body);
        toast.success('Hesap güncellendi');
      }
      close();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`"${item.label}" hesabını silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/postnl/credentials/${item.id}`);
      toast.success('Silindi');
      load();
    } catch {
      toast.error('Silme başarısız');
    }
  };

  const toggleActive = async (item) => {
    try {
      await api.patch(`/postnl/credentials/${item.id}`, { active: !item.active });
      load();
    } catch {
      toast.error('Güncelleme başarısız');
    }
  };

  return (
    <div className="space-y-6" data-testid="postnl-accounts-page">
      <PostnlPageTabs />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Key className="h-6 w-6 text-orange-500" />
            PostNL Hesapları
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Her firma için ayrı PostNL kullanıcı/şifre kaydı. Aktif olanlar Dagplanning ve Ritmonitor sayfalarında otomatik kullanılır.
          </p>
        </div>
        <Button
          onClick={openNew}
          className="bg-orange-500 hover:bg-orange-600 gap-2"
          data-testid="postnl-accounts-new-btn"
        >
          <Plus className="h-4 w-4" /> Yeni Hesap
        </Button>
      </div>

      {loading ? (
        <Card className="p-12 text-center">
          <Loader2 className="h-6 w-6 animate-spin text-orange-500 mx-auto" />
        </Card>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center" data-testid="postnl-accounts-empty">
          <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="text-sm text-slate-600 font-medium mb-1">Henüz hesap eklenmemiş</div>
          <div className="text-xs text-slate-400 mb-4">"Yeni Hesap" ile ilk PostNL kullanıcısını ekleyin.</div>
          <Button onClick={openNew} variant="outline" className="gap-2">
            <Plus className="h-4 w-4" /> Yeni Hesap
          </Button>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Firma</th>
                <th className="text-left px-3 py-2 font-semibold">Kullanıcı</th>
                <th className="text-left px-3 py-2 font-semibold">Şifre</th>
                <th className="text-left px-3 py-2 font-semibold">Notlar</th>
                <th className="text-center px-3 py-2 font-semibold">Durum</th>
                <th className="text-right px-3 py-2 font-semibold">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100 dark:border-slate-700 hover:bg-slate-50/60 dark:hover:bg-slate-800/40" data-testid={`postnl-account-row-${it.id}`}>
                  <td className="px-3 py-2 font-semibold">{it.label}</td>
                  <td className="px-3 py-2 font-mono text-xs">{it.username}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-400">{it.password_mask}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{it.notes || '—'}</td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => toggleActive(it)} title="Aktif/pasif" data-testid={`postnl-account-toggle-${it.id}`}>
                      {it.active
                        ? <Badge className="bg-emerald-100 text-emerald-700 border-0 gap-1"><CheckCircle2 className="h-3 w-3" /> Aktif</Badge>
                        : <Badge className="bg-slate-200 text-slate-600 border-0 gap-1"><XCircle className="h-3 w-3" /> Pasif</Badge>}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right space-x-1">
                    <button onClick={() => openEdit(it)} className="text-slate-500 hover:text-orange-600" title="Düzenle" data-testid={`postnl-account-edit-${it.id}`}>
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(it)} className="text-slate-500 hover:text-rose-600" title="Sil" data-testid={`postnl-account-delete-${it.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Edit/New modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={close}>
          <Card className="max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="postnl-account-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold flex items-center gap-2">
                <Key className="h-4 w-4 text-orange-500" />
                {editing === 'new' ? 'Yeni PostNL Hesabı' : `Düzenle: ${editing.label}`}
              </div>
              <button onClick={close} className="text-slate-400 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Firma Adı *</Label>
                <Input
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="FAST-TRANS, AYTU, HYK …"
                  data-testid="postnl-account-input-label"
                />
              </div>
              <div>
                <Label className="text-xs">PostNL Kullanıcı Adı *</Label>
                <Input
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="98140014"
                  data-testid="postnl-account-input-username"
                />
              </div>
              <div>
                <Label className="text-xs">
                  Şifre {editing !== 'new' && <span className="text-slate-400">(boş bırakırsanız değişmez)</span>}
                </Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={editing === 'new' ? 'PostNL şifresi' : '••••••••'}
                  data-testid="postnl-account-input-password"
                />
              </div>
              <div>
                <Label className="text-xs">Notlar</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="örn. Sint-Truiden depot"
                  data-testid="postnl-account-input-notes"
                />
              </div>
              <div className="flex items-center gap-3 pt-1">
                <Switch
                  checked={form.active}
                  onCheckedChange={(v) => setForm({ ...form, active: v })}
                  data-testid="postnl-account-switch-active"
                />
                <Label className="text-sm">Aktif (cron job ve dropdown'larda görünür)</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={close} disabled={saving}>İptal</Button>
              <Button
                onClick={save}
                disabled={saving}
                className="bg-orange-500 hover:bg-orange-600 gap-2"
                data-testid="postnl-account-save-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Kaydet
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
